import { useMemo, useState, useEffect, useCallback } from 'react';
import { AppBarDrawer } from '@centerpiece/components';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  SettingsOutlined,
  PersonOutlineOutlined,
  SlideshowOutlined,
  Logout,
  Schema,
  HelpCenterOutlined,
  CalendarMonthOutlined,
  BackupTable,
  LabelOutlined,
  ClassOutlined,
  Inventory2Outlined,
  ContactsOutlined,
  Workspaces,
  AnalyticsOutlined,
  TrendingUp,
  HighlightOutlined,
} from '@mui/icons-material';
import { useLogout } from 'utils/logout.utils';
import { SettingsTabDrawer } from 'drawers/SettingsTabDrawer';
import { AccountTabDrawer } from 'drawers/AccountTabDrawer';
import { indexSteps } from 'tutorials/indexpage.tutorial';
import { useTutorialContext } from 'tutorials/tutorial.context';
import { TutorialTabDrawer } from 'drawers/TutorialTabDrawer';
import { ContactList } from 'components/ContactList';
import { ProjectsIndexPage } from './index_pages/ProjectsIndexPage';
import { ItemsIndexPage } from './index_pages/ItemsIndexPage';
import { InactiveProjectsIndexPage } from './index_pages/InactiveProjectsIndexPage';
import { CalendarIndexPage } from './index_pages/CalendarIndexPage';
import { useIndexContext } from 'contexts/index.context';
import { AnalyticsIndexPage } from './index_pages/AnalyticsIndexPage';
import { OverviewIndexPage } from './index_pages/OverviewIndexPage';
import { usePatchContactMutation } from 'store/api';
import { Contact } from 'store/api/models';
import { useDispatch } from 'react-redux';
import { selectIndexHistory, setIndexHistory } from 'store/slice/history.slice';
import { useSelector } from 'react-redux';
import { Paper } from '@mui/material';
import { AnalyticProvider } from 'contexts/analytic.context';
import { BidAnalyticsIndexPage } from './index_pages/BidAnalyticsIndexPage';
import { UpliftAnalyticsIndexPage } from './index_pages/UpliftAnalyticsIndexPage';
import { StagesIndexPage } from './index_pages/StagesIndexPage';

export const NewIndexPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const onLogout = useLogout();
  const { contactRecord } = useIndexContext();
  const lastTab = useSelector(selectIndexHistory);
  const [patchContact, patchContactResult] = usePatchContactMutation();
  const [openDialog, setOpenDialog] = useState<string>();
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [openAccountDrawer, setOpenAccountDrawer] = useState<boolean>(false);
  const [openTutorialDrawer, setOpenTutorialDrawer] = useState<boolean>(false);
  const location = useLocation();
  const [initialTab, setInitialTab] = useState<string>();
  const { state, setState } = useTutorialContext();
  useEffect(() => {
    if (state.tourActive) {
      setState(currState => ({
        ...currState,
        run: true,
      }));
    }
  }, []);
  useEffect(() => {
    if (location.hash !== '') {
      //clear hash after reading
      const itemMatch = location?.hash?.match(/#at=(\w+)/);
      window.history.replaceState(null, '', location.pathname);
      setInitialTab(itemMatch?.[1] || '');
    }
    // eslint-disable-next-line
  }, [location.hash]);

  return (
    <AppBarDrawer
      initialTab={initialTab || lastTab}
      defaultTab={useMemo(
        () => contactRecord?.form?.appMenu?.defaultTab || '',
        [contactRecord?.form?.appMenu?.defaultTab]
      )}
      onMenuChange={(index: string) => {
        dispatch(setIndexHistory(index));
      }}
      onConfigChange={useCallback(
        async (tab: string) => {
          await patchContact({
            id: contactRecord.id,
            form: { appMenu: { defaultTab: tab } },
          } as Contact);
        },
        [contactRecord.id]
      )}
      configLoading={patchContactResult.isLoading}
      extras={[
        <>
          <AccountTabDrawer
            open={openAccountDrawer}
            onClose={() => {
              setOpenAccountDrawer(false);
            }}
          />
          <TutorialTabDrawer
            open={openTutorialDrawer}
            onClose={() => {
              setOpenTutorialDrawer(false);
            }}
          />
          {contactRecord?.form?.admin === 'Yes' && (
            <SettingsTabDrawer
              open={openDrawer}
              onClose={() => {
                setOpenDrawer(false);
              }}
            />
          )}
        </>,
      ]}
      tabs={{
        ...(contactRecord?.form?.authorization === 'Executive' ||
        contactRecord?.form?.authorization === 'Team Leader'
          ? {
              overview: {
                index: 'overview',
                title: 'Overview',
                icon: <HighlightOutlined />,
                content: <OverviewIndexPage />,
                sequence: 0,
              },
            }
          : {}),
        projects: {
          index: 'projects',
          title: 'Projects',
          icon: <BackupTable />,
          content: <ProjectsIndexPage />,
          sequence: 1,
        },
        items: {
          index: 'items',
          title: 'Items',
          icon: <LabelOutlined />,
          content: <ItemsIndexPage />,
          sequence: 2,
        },
        // ...(contactRecord?.form?.authorization === 'Team Leader' ||
        // contactRecord?.form?.authorization === 'Executive'
        //   ? {
        //       stages: {
        //         index: 'stages',
        //         title: 'Stages',
        //         icon: <ClassOutlined />,
        //         content: <StagesIndexPage />,
        //         sequence: 2,
        //       },
        //     }
        //   : {}),
        calendar: {
          index: 'calendar',
          title: 'Calendar',
          icon: <CalendarMonthOutlined />,
          content: <CalendarIndexPage />,
          sequence: 3,
        },
        contacts: {
          index: 'contacts',
          icon: <ContactsOutlined />,
          title: 'Contacts',
          backgroundColor: 'white',
          // contents: <ContactsTab />,
          content: (
            <Paper elevation={5} sx={{ p: 1, my: 1 }}>
              <ContactList />
            </Paper>
          ),
          sequence: 4,
        },
        inactive: {
          index: 'inactive',
          title: 'Inactive Projects',
          icon: <Inventory2Outlined />,
          content: <InactiveProjectsIndexPage />,
          sequence: 5,
        },
        ...(contactRecord?.form?.authorization === 'Executive' ||
        contactRecord?.form?.authorization === 'Team Leader'
          ? {
              analytics: {
                index: 'analytics',
                title: 'Bid Analytics',
                icon: <AnalyticsOutlined />,
                content: (
                  <AnalyticProvider bid>
                    {' '}
                    <BidAnalyticsIndexPage />
                  </AnalyticProvider>
                ),
                sequence: 6,
              },
              uplift: {
                index: 'uplift',
                title: 'Uplift Analytics',
                icon: <TrendingUp />,
                content: (
                  <AnalyticProvider>
                    {' '}
                    <UpliftAnalyticsIndexPage />
                  </AnalyticProvider>
                ),
                sequence: 6,
              },
            }
          : {}),
      }}
      headerProps={{
        tooltip: 'Menu',
        menuItems: [
          ...(contactRecord?.form?.admin === 'Yes'
            ? [
                {
                  icon: SettingsOutlined,
                  label: 'Settings',
                  onClick: () => setOpenDrawer(true),
                  type: 'buttonitem',
                },
              ]
            : ([] as any)),
          {
            icon: PersonOutlineOutlined,
            label: 'Account',
            onClick: () => setOpenAccountDrawer(true),
            type: 'buttonitem',
          },
          {
            icon: HelpCenterOutlined,
            label: 'Tutorial Videos',
            onClick: () => setOpenTutorialDrawer(true),
            type: 'buttonitem',
          },
          {
            icon: SlideshowOutlined,
            label: 'Tutorial Walkthrough',
            onClick: () =>
              setState(currState => ({
                run: true,
                steps: indexSteps() as any,
                alias: 'IndexPage',
                triggers: {
                  ...currState.triggers,
                  close: () => {
                    currState.triggers?.close();
                    setOpenDialog(undefined);
                    setOpenDrawer(false);
                    navigate('/');
                  },
                  openCreateProjectDialog: () => setOpenDialog('FES'),
                  openOtherProjectDialog: () => setOpenDialog('Custom'),
                  closeCreateProjectDialog: () => setOpenDialog(undefined),
                  openSettingsDrawer: () => setOpenDrawer(true),
                  closeSettingsDrawer: () => setOpenDrawer(false),
                  exampleProject: () => navigate('/project/example'),
                  indexPage: () => navigate('/'),
                },
                stepIndex: 0,
              })),
            type: 'buttonitem',
          },
          ...(contactRecord?.enterprise?.form?.workspace
            ? [
                {
                  icon: Workspaces,
                  label: 'Centerpiece Workspace',
                  onClick: () =>
                    window.open(
                      contactRecord?.enterprise?.form?.workspace,
                      '_blank'
                    ),
                },
              ]
            : []),
          {
            icon: Schema,
            label: 'Project Templates',
            onClick: () => navigate('/project_template'),
          },
          { icon: Logout, label: 'Logout', onClick: () => onLogout() },
        ],
        moreIconButtonProps: { size: 'medium' },
      }}
    />
  );
};
